import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setUserWithTokens } from '@ackee/petrus';
import { FlowType } from '@ackee/petrus';

import config from 'config';
import { SALESFORCE_ACCESS_TOKEN_STORAGE_KEY } from '../../constantsSalesforce';
import { authFlowTypeSelector } from '../../services/selectors';

function decodeJwtExp(token: string): number | null {
    try {
        const [, payload] = token.split('.');
        if (!payload) return null;
        const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
        const json = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
                .join(''),
        );
        const parsed = JSON.parse(json) as { exp?: number };
        return typeof parsed.exp === 'number' ? parsed.exp : null;
    } catch {
        return null;
    }
}

function normalizeApprovedUsers(value: unknown): string[] {
    const raw =
        Array.isArray(value) ? value
        : (value as any)?.approvedUsers ?? (value as any)?.data ?? (value as any)?.users ?? [];

    if (!Array.isArray(raw)) return [];
    return raw.filter((x) => typeof x === 'string') as string[];
}

function normalizeSalesforceUser(value: unknown): Record<string, unknown> {
    if (Array.isArray(value)) return (value[0] ?? {}) as Record<string, unknown>;
    if (value && typeof value === 'object') return value as Record<string, unknown>;
    return {};
}

export default function SalesforceSessionBootstrap() {
    const dispatch = useDispatch();
    const flowType = useSelector(authFlowTypeSelector);

    useEffect(() => {
        // Wait until Petrus finished determining auth flow.
        if (flowType === FlowType.INDETERMINATE) return;

        // If already authenticated, do nothing.
        if (flowType === FlowType.AUTHENTICATED) return;

        // Only bootstrap when Petrus thinks we are anonymous.
        if (flowType !== FlowType.ANONYMOUS) return;

        const token = localStorage.getItem(SALESFORCE_ACCESS_TOKEN_STORAGE_KEY);
        if (!token) return;

        let cancelled = false;

        (async () => {
            try {
                // Verify token and get base identity (emailId/userId etc.)
                const verifyRes = await fetch(`${config.api.base}${config.api.salesforceMagicLoginVerify}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ token }),
                });
                const rawUser = await verifyRes.json().catch(() => null);
                if (!verifyRes.ok || !rawUser) throw new Error('verify_failed');

                const email = rawUser?.emailId ?? rawUser?.email;
                if (!email) throw new Error('missing_email');

                const [salesforceUserRes, approvedUsersRes] = await Promise.all([
                    fetch(`${config.api.base}${config.api.users}/${encodeURIComponent(String(email))}`, {
                        headers: { Authorization: `Bearer ${token}` },
                    }),
                    fetch(`${config.api.base}${config.api.users}/document/approved`, {
                        headers: { Authorization: `Bearer ${token}` },
                    }),
                ]);

                const salesforceUserRaw = await salesforceUserRes.json().catch(() => ({}));
                const approvedUsersRaw = await approvedUsersRes.json().catch(() => ([]));

                const salesforceUser = normalizeSalesforceUser(salesforceUserRaw);
                const approvedUsers = normalizeApprovedUsers(approvedUsersRaw);

                const exp = decodeJwtExp(token);
                const expiration = new Date(((exp ?? Math.floor(Date.now() / 1000) + 60 * 60) as number) * 1000).toISOString();

                const user = {
                    username: rawUser?.userId ?? String(email),
                    customUserId: rawUser?.userId,
                    email: String(email),
                    ...rawUser,
                    ...salesforceUser,
                    approvedUsers,
                };

                if (cancelled) return;
                dispatch(setUserWithTokens(user, {
                    accessToken: { token, expiration },
                    refreshToken: { token: null },
                }));
            } catch {
                // Token is invalid/expired -> clear it so we don't loop.
                localStorage.removeItem(SALESFORCE_ACCESS_TOKEN_STORAGE_KEY);
            }
        })();

        return () => { cancelled = true; };
    }, [dispatch, flowType]);

    return null;
}

